import { type QuestionType } from "~/types/quiz";

export type TherapistMatchingAnswer = string | string[];

export type TherapistMatchingQuestion = {
  id: string;
  section: "potrebe" | "pristup" | "prakticno" | "dodatno";
  type: QuestionType;
  question: string;
  helperText?: string;
  placeholder?: string;
  options?: string[];
  optional?: boolean;
  showWhen?: (answers: Record<string, TherapistMatchingAnswer>) => boolean;
};

const individualQuestions: TherapistMatchingQuestion[] = [
  {
    id: "focus_areas",
    section: "potrebe",
    type: "checkbox",
    question: "Šta te trenutno najviše muči?",
    helperText: "Možeš izabrati više odgovora.",
    options: [
      "Anksioznost, strah ili napadi panike",
      "Raspoloženje, tuga ili manjak energije",
      "Odnosi sa partnerom, porodicom ili drugima",
      "Posao, fakultet ili sagorevanje",
      "Samopouzdanje, granice ili identitet",
      "Trauma, gubitak ili velika životna promena",
      "Spavanje, navike ili motivacija",
      "Nešto drugo",
    ],
  },
  {
    id: "daily_impact",
    section: "potrebe",
    type: "radio",
    question: "Koliko često ti to otežava svakodnevicu?",
    options: ["Ponekad", "Često", "Gotovo svaki dan"],
  },
  {
    id: "therapy_before",
    section: "potrebe",
    type: "radio",
    question: "Da li si ranije išao/la na psihoterapiju?",
    options: ["Da", "Ne"],
  },
  {
    id: "therapy_before_context",
    section: "potrebe",
    type: "textarea",
    question: "Šta ti je u prethodnoj terapiji prijalo, a šta nije?",
    helperText: "Odgovori samo ako želiš.",
    placeholder:
      "Na primer, prijali su mi praktični zadaci ili mi je nedostajalo više direktnosti.",
    optional: true,
    showWhen: (answers) => answers.therapy_before === "Da",
  },
  {
    id: "reason",
    section: "potrebe",
    type: "textarea",
    question: "Zbog čega baš sada tražiš terapeuta?",
    helperText: "Napiši onoliko koliko ti je prijatno.",
    placeholder: "Šta ti je trenutno najvažnije da promeniš ili razumeš?",
  },
  {
    id: "goals",
    section: "potrebe",
    type: "textarea",
    question: "Šta želiš da bude drugačije?",
    placeholder:
      "Na primer, da se osećam mirnije, postavim granice ili bolje razumem sebe.",
  },
  {
    id: "therapist_gender",
    section: "pristup",
    type: "radio",
    question: "Da li ti je važan pol terapeuta?",
    options: ["Svejedno mi je", "Želim terapeutkinju", "Želim terapeuta"],
  },
  {
    id: "therapist_style",
    section: "pristup",
    type: "radio",
    question: "Kakav pristup terapeuta bi ti najviše odgovarao?",
    options: [
      "Topao i podržavajući",
      "Miran i strpljiv",
      "Kroz pitanja koja podstiču na razmišljanje",
      "Praktičan, sa konkretnim koracima i vežbama",
      "Direktan, usmeren na promenu",
      "Važno mi je da se prilagodi meni",
    ],
  },
  {
    id: "therapy_preferences",
    section: "pristup",
    type: "checkbox",
    question: "Šta ti je najvažnije u radu sa terapeutom?",
    helperText: "Možeš izabrati više odgovora.",
    options: [
      "Da me pažljivo sasluša",
      "Da postavlja pitanja koja me navode na razmišljanje",
      "Da zajedno pravimo konkretne korake",
      "Da bolje razumem prošla iskustva",
      "Da dobijem vežbe ili zadatke između sesija",
    ],
  },
  {
    id: "cultural_context",
    section: "pristup",
    type: "radio",
    question:
      "Da li je važno da terapeut razume neki deo tvog životnog iskustva?",
    helperText:
      "Na primer, život u dijaspori, religija ili porodični kontekst.",
    options: ["Da", "Ne", "Nisam siguran/na"],
  },
  {
    id: "cultural_context_details",
    section: "pristup",
    type: "textarea",
    question: "Šta je važno da terapeut razume?",
    placeholder: "Napiši samo ono što želiš da podeliš.",
    optional: true,
    showWhen: (answers) => answers.cultural_context === "Da",
  },
  {
    id: "budget",
    section: "prakticno",
    type: "radio",
    question: "Koja cena po sesiji ti odgovara?",
    helperText: "Prikazaćemo terapeute koji se uklapaju u tvoj budžet.",
    options: [
      "Do 35 €",
      "Do 45 €",
      "Do 55 €",
      "Do 70 €",
      "Cena mi nije presudna",
    ],
  },
  {
    id: "anything_else",
    section: "dodatno",
    type: "textarea",
    question: "Želiš li još nešto da nam kažeš?",
    helperText: "Odgovori samo ako želiš.",
    placeholder: "Možeš preskočiti ovo pitanje.",
    optional: true,
  },
];

const couplesQuestions: TherapistMatchingQuestion[] = [
  {
    id: "focus_areas",
    section: "potrebe",
    type: "checkbox",
    question: "Šta vas trenutno najviše muči u odnosu?",
    helperText: "Možete izabrati više odgovora.",
    options: [
      "Komunikacija i česti konflikti",
      "Poverenje ili neverstvo",
      "Emocionalno udaljavanje",
      "Intimnost",
      "Roditeljstvo ili porodični odnosi",
      "Novac, posao ili svakodnevne obaveze",
      "Različite životne želje",
      "Nešto drugo",
    ],
  },
  {
    id: "relationship_duration",
    section: "potrebe",
    type: "radio",
    question: "Koliko dugo ste zajedno?",
    options: [
      "Manje od godinu dana",
      "1 do 5 godina",
      "5 do 10 godina",
      "Više od 10 godina",
    ],
  },
  {
    id: "therapy_before",
    section: "potrebe",
    type: "radio",
    question: "Da li ste ranije išli na terapiju za parove?",
    options: ["Da", "Ne"],
  },
  {
    id: "therapy_before_context",
    section: "potrebe",
    type: "textarea",
    question: "Šta vam je u prethodnoj terapiji prijalo, a šta nije?",
    helperText: "Odgovorite samo ako želite.",
    placeholder: "Napišite samo ono što može pomoći da vas bolje razumemo.",
    optional: true,
    showWhen: (answers) => answers.therapy_before === "Da",
  },
  {
    id: "reason",
    section: "potrebe",
    type: "textarea",
    question: "Zbog čega baš sada tražite terapeuta?",
    helperText: "Napišite onoliko koliko vam je prijatno.",
    placeholder: "Šta je sada najvažnije za vaš odnos?",
  },
  {
    id: "goals",
    section: "potrebe",
    type: "textarea",
    question: "Šta želite da bude drugačije u vašem odnosu?",
    placeholder:
      "Na primer, da razgovaramo bez svađe ili vratimo osećaj bliskosti.",
  },
  {
    id: "therapist_gender",
    section: "pristup",
    type: "radio",
    question: "Da li vam je važan pol terapeuta?",
    options: ["Svejedno nam je", "Želimo terapeutkinju", "Želimo terapeuta"],
  },
  {
    id: "therapist_style",
    section: "pristup",
    type: "radio",
    question: "Kakav pristup terapeuta bi vam najviše odgovarao?",
    options: [
      "Topao i podržavajući",
      "Miran i strpljiv",
      "Kroz pitanja koja podstiču na razmišljanje",
      "Praktičan, sa konkretnim koracima i vežbama",
      "Direktan, usmeren na promenu",
      "Važno nam je da se prilagodi nama",
    ],
  },
  {
    id: "therapy_preferences",
    section: "pristup",
    type: "checkbox",
    question: "Šta vam je najvažnije u radu sa terapeutom?",
    helperText: "Možete izabrati više odgovora.",
    options: [
      "Da nam pomogne da razgovaramo mirnije",
      "Da postavlja pitanja koja otvaraju važne teme",
      "Da dobijemo konkretne vežbe",
      "Da bolje razumemo obrazac konflikata",
      "Da zajedno postavimo zajedničke ciljeve",
    ],
  },
  {
    id: "budget",
    section: "prakticno",
    type: "radio",
    question: "Koja cena po sesiji vam odgovara?",
    helperText: "Prikazaćemo terapeute koji se uklapaju u vaš budžet.",
    options: [
      "Do 45 €",
      "Do 55 €",
      "Do 70 €",
      "Do 85 €",
      "Cena nam nije presudna",
    ],
  },
  {
    id: "anything_else",
    section: "dodatno",
    type: "textarea",
    question: "Želite li još nešto da nam kažete?",
    helperText: "Odgovorite samo ako želite.",
    placeholder: "Možete preskočiti ovo pitanje.",
    optional: true,
  },
];

export const matchTherapistQuestions = individualQuestions;
export const matchTherapistCouplesQuestions = couplesQuestions;
